import { CheckReturnObjectType } from '../../interface/v1/Criterion/types';
import StageBuilder from './stageBuilder/StageBuilder';
import * as T from './types';
import matchPath from '../../utils/matchPath';
import IMfeRouterService from './IMfeRouterService';
import { SetServiceDependencies } from '../../infra/startup/types';
import SceneReporter from './sceneReporter/SceneReporter';
import * as OperationFactory from './daemons/OperationFactory';
import Criterion from '../../interface/v1/Criterion';
import { FallbackInterfaceType } from '../../interface/v1/Fallback/types';
import bindAllMethods from '../../utils/bindAllMethods';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import EventNames from '../../config/eventNames';
import { LOGIN_AND_LOGGEDOUT_PATH_LIST } from '../../services/session/constants';
import { InternalService } from '../../core/services';
import IEventService from '../eventService/IEventService';
import { ICriterionService } from '../criterionService/ICriterionService';

export default class MicrofrontendRouterService
  extends InternalService
  implements IMfeRouterService
{
  private criterionInterface: Criterion<any>;
  private fallbackInterface: FallbackInterfaceType;
  private navigationInterface: T.ChainOfOperationsInputType['navigationInterface'];
  private storeInterface: T.ChainOfOperationsInputType['storeInterface'];
  private authProviderInterface: T.ChainOfOperationsInputType['authProviderInterface'];
  private localizationInterface: T.ChainOfOperationsInputType['localizationInterface'];

  public operations;
  public eventName = EventNames.globalMicrofrontendRouter;

  private _sceneReporter;
  private _tenantHandlerService;
  private _eventService: IEventService;

  private started = false;
  private state: T.SceneType = {};
  private updateCurrentMicrofrontendPromiseId = 0;
  private setStatePromiseId = 0;

  private _criterionService: ICriterionService;
  private listeners: Array<(state: T.SceneType) => void> = [];

  constructor() {
    super();
    bindAllMethods(this);
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._eventService = services.eventService;
    this._tenantHandlerService = services.tenantHandlerService;
    this.fallbackInterface = services.fallbackService as any;
    this._criterionService = services.criterionService;
  }

  public setInterfaceDependencies(
    options: T.MicrofrontendRouterDependenciesType
  ): void {
    // The wall of shame of interfaces
    this.storeInterface = options.interfaces.store;
    this.navigationInterface = options.interfaces.navigation;
    this.fallbackInterface = options.interfaces.fallback;
    this.criterionInterface = options.interfaces.criterion;
    this.authProviderInterface = options.interfaces.authProvider;
    this.localizationInterface = options.interfaces.localization;
  }

  public async init(data?: T.OperationDataInputType): Promise<void> {
    this.criterionInterface = this._criterionService.getCriterionInstance();

    if (!data) {
      internalLogger?.warn?.(
        'The mandatory data to start MFERouterService is missing.'
      );
      this._markAsReady();
      return;
    }

    // Execute initialization commands
    await this._initializeWaitForEventService();
    await this._initializeSceneReporter();
    await this._initializeOperations(data);

    this._markAsReady();
  }

  /**
   * Command: Wait for event service to be ready
   */
  private async _initializeWaitForEventService(): Promise<void> {
    await this._eventService.isReady;
  }

  /**
   * Command: Create and configure the scene reporter
   */
  private async _initializeSceneReporter(): Promise<void> {
    // These init methods allow to async bootstrapping the services.
    // Here, it needs the interfaces already working.
    // Composition Design Pattern.
    this._sceneReporter = new SceneReporter({
      fallbackInterface: this.fallbackInterface,
      criterionInterface: this.criterionInterface,
      storeInterface: this.storeInterface,
      navigationInterface: this.navigationInterface,
      eventService: this._eventService
    });
  }

  /**
   * Command: Create standard operation flow
   */
  private async _initializeOperations(
    data: T.OperationDataInputType
  ): Promise<void> {
    const operations = OperationFactory.CreateStandardOperationFlow({
      data: data,
      storeInterface: this.storeInterface,
      navigationInterface: this.navigationInterface,
      fallbackInterface: this.fallbackInterface,
      authProviderInterface: this.authProviderInterface,
      localizationInterface: this.localizationInterface,
      criterionInterface: this.criterionInterface
    });
    this.operations = operations;
  }

  // TODO: From here down, everything could be reworked to use more Composition approach
  // TODO: This method exists only because onboarding agent is initialized after shell-commons
  // it should be removed on future when we have a centralized solution
  async start(): Promise<void> {
    if (!this.started && !!this._eventService) {
      const syncData = async () => {
        await this.updateState();
      };

      this.started = true;

      this._sceneReporter.setEventListeners(syncData);

      return syncData();
    }
  }

  public async checkCriterion(
    criterionKey?: string | false
  ): Promise<CheckReturnObjectType> {
    if (!criterionKey)
      return {
        result: true
      };

    const criterion =
      await this.criterionInterface.checkAdditionalCriterionDataByKey(
        criterionKey
      );

    return criterion;
  }

  private async setState(data: T.StageType) {
    internalLogger?.log?.(
      'resolving mfeRouterService.setState to ',
      JSON.stringify(data)
    );
    const thisPromiseId = this.setStatePromiseId + 1;
    this.setStatePromiseId = thisPromiseId;
    const stateRedirectTo = data?.redirectTo || '';

    if (stateRedirectTo) {
      // TODO: this should be the navigationService
      const pathToCompare = this.navigationInterface.location.pathname;
      const isSamePath = matchPath(stateRedirectTo, {
        exact: true,
        pathToCompare
      });

      const isFullUrl =
        stateRedirectTo.startsWith('http://') ||
        stateRedirectTo.startsWith('https://');

      const isLoginPath = matchPath(LOGIN_AND_LOGGEDOUT_PATH_LIST, {
        exact: true,
        pathToCompare: stateRedirectTo
      });

      // Full URL redirects and login paths require hard navigation (page unload)
      if (isFullUrl || isLoginPath) {
        const redirectPromise = new Promise(() => {
          this.navigationInterface.redirect(stateRedirectTo);
        });
        await redirectPromise;
      }
      if (!isSamePath) {
        this.navigationInterface.push(stateRedirectTo);
      }
    } else {
      const tenantHandlerOverride = data?.content?.tenantHandlerOverride;
      this._tenantHandlerService.setTenantHandlerKey(tenantHandlerOverride);

      const contentCriterion = await this.checkCriterion(
        data?.content?.criterionKey
      );

      if (!contentCriterion?.result) {
        this.fallbackInterface.setCurrentFallbackByKey(
          contentCriterion?.fallbackKey
        );
      }

      const getMicrofrontendObject = async (
        newMicrofrontendRouterAsset: T.MicrofrontendRouterAssetType,
        currentMicrofrontendRouterAsset: T.MicrofrontendRouterAssetType
      ) => {
        const criterion = await this.checkCriterion(
          newMicrofrontendRouterAsset?.criterionKey
        );

        if (criterion?.result) {
          // Keep old object reference if the new object have the same values
          const isDifferentValue =
            JSON.stringify(newMicrofrontendRouterAsset) !==
            JSON.stringify(currentMicrofrontendRouterAsset);

          if (isDifferentValue) {
            return newMicrofrontendRouterAsset;
          } else {
            return currentMicrofrontendRouterAsset;
          }
        }
      };

      const newState: T.SceneType = {
        content: await getMicrofrontendObject(
          data?.content,
          this.state?.content
        ),
        layout: await getMicrofrontendObject(data?.layout, this.state?.layout),
        modalContent: await getMicrofrontendObject(
          data?.modalContent,
          this.state?.modalContent
        )
      };

      const isLastPromise = thisPromiseId === this.setStatePromiseId;
      const isDifferentValue =
        JSON.stringify(newState) !== JSON.stringify(this.state);

      if (isLastPromise && isDifferentValue) {
        this.state = newState;
        this._eventService.publish(this.eventName, { data: this.state });
        this.notifyListeners();
      }
    }
  }

  private async updateState() {
    const stateBuilder = new StageBuilder({});
    const thisPromiseId = this.updateCurrentMicrofrontendPromiseId + 1;
    this.updateCurrentMicrofrontendPromiseId = thisPromiseId;

    const recursiveOperationProcess = async (
      operations: T.RouterOperationInterface[],
      promiseId: number
    ) => {
      const [thisOperation, ...nextOperations] = operations;

      internalLogger?.debug?.d0?.({
        message: '[MFERouter] processing operation',
        promiseId,
        currentOperation: thisOperation?.constructor?.name
      });

      try {
        await thisOperation?.process?.(stateBuilder);
      } catch (error) {
        internalLogger?.error?.e3?.({
          className: 'MicrofrontendRouterService',
          methodName: 'updateState',
          message: '[MFERouter] operation process error',
          currentOperation: thisOperation?.constructor?.name,
          promiseId,
          error
        });
        throw error;
      }

      const thisState = stateBuilder.getState();

      if (
        !thisState?.endProcessChain &&
        Array.isArray(nextOperations) &&
        nextOperations.length > 0 &&
        this.updateCurrentMicrofrontendPromiseId === promiseId
      ) {
        await recursiveOperationProcess(nextOperations, promiseId);
      }
    };

    await recursiveOperationProcess(this.operations, thisPromiseId);

    if (this.updateCurrentMicrofrontendPromiseId === thisPromiseId) {
      const newState = stateBuilder.getState();
      internalLogger?.debug?.d0?.({
        message: '[MFERouter] updateState ended',
        promiseId: thisPromiseId,
        assetReference: newState?.content?.assetReference
      });
      await this.setState(newState);
    }
  }

  public getState(): T.SceneType {
    return this.state;
  }

  /**
   * This method allows to subscribe to changes in the MFE router state by
   * providing a callback function. THe callback function receives the current
   * MFE state.
   * @param callback
   * @returns
   */
  public listen(callback: (state: T.SceneType) => void): () => void {
    this.listeners.push(callback);
    const callbackProxy = async () => {
      callback?.(this.getState());
    };

    this._eventService?.addEventListener?.(this.eventName, callbackProxy);

    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
      this._eventService?.removeEventListener?.(this.eventName, callbackProxy);
    };
  }

  private notifyListeners() {
    this.listeners.forEach((cb) => cb(this.getState()));
  }

  // TODO: This could be derived from "an implements of UseReactHookable" interface
  public useReactHook(React: any): T.SceneType {
    const [state, setState] = React.useState(this.getState());

    React.useEffect(() => {
      const removeListener = this.listen((value) => setState(value));

      return () => removeListener();
    }, []);

    return state;
  }
}
